import type { OCRInstance, OCRResult } from '@/types';
import { OCR_QUOTA_THRESHOLD } from '@/lib/constants';
import { useConfigStore } from '@/store/useConfigStore';

export function isLocalOCRInstance(instance: OCRInstance): boolean {
    return instance.type === 'LocalTesseract' || instance.apiUrl.startsWith('local://');
}

// 选择可用的 OCR 实例 (轮询调度)
export function selectOCRInstance(instances: OCRInstance[]): OCRInstance | null {
    const enabledInstances = instances.filter(inst => inst.enabled);
    if (enabledInstances.length === 0) return null;

    const localInstance = enabledInstances.find(isLocalOCRInstance);
    if (localInstance) return localInstance;

    // 优先使用未达配额 90% 的实例
    const available = enabledInstances.filter(
        inst => inst.quota > 0 && inst.usedCount / inst.quota < OCR_QUOTA_THRESHOLD
    );

    if (available.length > 0) {
        // 返回使用次数最少的可用实例
        return available.reduce((min, inst) => inst.usedCount < min.usedCount ? inst : min, available[0]);
    }

    // 全部达到 90%，返回使用次数最少的启用实例
    return enabledInstances.reduce((min, inst) => inst.usedCount < min.usedCount ? inst : min, enabledInstances[0]);
}

// 构建代理 URL（解决浏览器 CORS 限制）
function proxyUrl(target: string): string {
    return `/api/proxy?target=${encodeURIComponent(target)}`;
}

export interface OCRCallOptions {
    trackUsage?: boolean;
    getRemoteImageUrl?: () => Promise<string>;
}

// 调用单个 OCR 实例
async function callOCRRequest(instance: OCRInstance, imageUrl: string, options?: OCRCallOptions): Promise<OCRResult> {
    if (isLocalOCRInstance(instance)) {
        const { callLocalOCR } = await import('@/services/localOCRService');
        const result = await callLocalOCR(imageUrl);
        if (result.success && options?.trackUsage !== false) useConfigStore.getState().incrementOCRUsage(instance.id);
        return result;
    }

    try {
        // PaddleOCR API: 先提交任务，再轮询结果
        const submitUrl = proxyUrl(instance.apiUrl);

        const submitResponse = await fetch(submitUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `bearer ${instance.token}`,
            },
            body: JSON.stringify({
                fileUrl: imageUrl,
                model: instance.model,
                optionalPayload: {
                    useDocOrientationClassify: false,
                    useDocUnwarping: false,
                    useChartRecognition: false,
                },
            }),
        });

        if (!submitResponse.ok) {
            const errorText = await submitResponse.text().catch(() => '');
            // 尝试解析代理返回的图片下载失败错误
            if (submitResponse.status === 502) {
                try {
                    const errJson = JSON.parse(errorText);
                    if (errJson.error?.includes('Image download failed')) {
                        return { success: false, text: '', error: `图片下载失败，服务器无法访问该图片 URL` };
                    }
                } catch { /* ignore */ }
            }
            return { success: false, text: '', error: `OCR 提交失败 (${submitResponse.status}): ${errorText}` };
        }

        const submitData = await submitResponse.json();
        const jobId = submitData?.data?.jobId || submitData?.data?.job_id || submitData?.jobId;

        if (!jobId) {
            return { success: false, text: '', error: 'OCR 未返回任务 ID' };
        }

        // 轮询任务结果
        const resultUrl = proxyUrl(`${instance.apiUrl}/${jobId}`);
        const maxRetries = 60; // 最多轮询 60 次
        const pollInterval = 5000; // 5 秒

        for (let i = 0; i < maxRetries; i++) {
            await new Promise(resolve => setTimeout(resolve, pollInterval));

            const resultResponse = await fetch(resultUrl, {
                headers: {
                    'Authorization': `bearer ${instance.token}`,
                },
            });

            if (!resultResponse.ok) continue;

            const resultData = await resultResponse.json();
            const state = resultData?.data?.state;

            if (state === 'done') {
                const text = await extractOCRText(resultData, instance.token);
                if (options?.trackUsage !== false) useConfigStore.getState().incrementOCRUsage(instance.id);
                return { success: true, text, raw: resultData };
            }

            if (state === 'failed') {
                const errorMsg = resultData?.data?.errorMsg || '未知错误';
                return { success: false, text: '', error: `OCR 任务执行失败: ${errorMsg}` };
            }
            // pending / running → 继续轮询
        }

        return { success: false, text: '', error: 'OCR 任务超时' };
    } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        return { success: false, text: '', error: `OCR 调用异常: ${message}` };
    }
}

// 本地 OCR 优先；本地识别失败时自动回退到第一个启用的远程实例。
export async function callOCR(instance: OCRInstance, imageUrl: string, options?: OCRCallOptions): Promise<OCRResult> {
    let activeInstance = instance;
    let remoteImagePromise: Promise<string> | null = null;
    const imageFor = async (candidate: OCRInstance): Promise<string> => {
        if (isLocalOCRInstance(candidate) || !options?.getRemoteImageUrl) return imageUrl;
        remoteImagePromise ??= options.getRemoteImageUrl();
        return remoteImagePromise;
    };

    let result = await callOCRRequest(activeInstance, await imageFor(activeInstance), options);
    if (!result.success && isLocalOCRInstance(activeInstance)) {
        const fallback = useConfigStore.getState().ocrInstances.find(candidate => candidate.enabled && !isLocalOCRInstance(candidate));
        if (fallback) {
            activeInstance = fallback;
            result = await callOCRRequest(activeInstance, await imageFor(activeInstance), options);
        }
    }

    return { ...result, instanceId: activeInstance.id, instanceName: activeInstance.name };
}

// 从 OCR 响应中提取识别文本
async function extractOCRText(data: unknown, token: string): Promise<string> {
    const result = data as Record<string, unknown>;
    const dataField = result?.data as Record<string, unknown> | undefined;

    // PaddleOCR-VL 返回格式: data.resultUrl.jsonUrl → JSONL → layoutParsingResults → markdown.text
    const resultUrlObj = dataField?.resultUrl as Record<string, unknown> | undefined;
    if (resultUrlObj?.jsonUrl && typeof resultUrlObj.jsonUrl === 'string') {
        try {
            const jsonlResponse = await fetch(proxyUrl(resultUrlObj.jsonUrl));
            if (jsonlResponse.ok) {
                const jsonlText = await jsonlResponse.text();
                const lines = jsonlText.trim().split('\n').filter(l => l.trim());
                const textParts: string[] = [];
                for (const line of lines) {
                    try {
                        const parsed = JSON.parse(line);
                        const layoutResults = parsed?.result?.layoutParsingResults;
                        if (Array.isArray(layoutResults)) {
                            for (const lr of layoutResults) {
                                const md = lr?.markdown?.text;
                                if (md) textParts.push(md);
                            }
                        }
                    } catch { /* skip invalid lines */ }
                }
                if (textParts.length > 0) return textParts.join('\n');
            }
        } catch { /* fallback to other formats */ }
    }

    // 尝试从直接返回的结果中提取
    const results = result?.results || dataField?.results || result?.data;
    if (Array.isArray(results)) {
        return results.map((item: unknown) => {
            if (typeof item === 'string') return item;
            const r = item as Record<string, unknown>;
            return r?.rec_text || r?.text || r?.content || '';
        }).join(' ');
    }
    if (typeof results === 'object' && results !== null) {
        const r = results as Record<string, unknown>;
        if (r.rec_texts && Array.isArray(r.rec_texts)) {
            return r.rec_texts.join(' ');
        }
    }
    return '';
}

// 检查文本是否包含任一关键词
export function containsAnyKeyword(text: string, keywords: string[]): string[] {
    const lowerText = text.toLowerCase();
    return keywords.filter(kw => lowerText.includes(kw.toLowerCase()));
}
